// src/pages/markets/board/api/useColumnEvents.ts

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { keepPreviousData, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBoardRank, MAX_RANK_LIMIT } from './boardApi';
import { boardKeys, filterSignature } from './boardKeys';
import { getEventBatcher } from './eventBatcher';
import type { BoardFilters, BoardRankResponse, BoardRankRow, ScreenerEvent } from './boardTypes';
import { PAGE_SIZE } from '../board/columnConfig';

const RANK_STALE_MS = 55_000;
const RANK_GC_MS = 10 * 60_000;
const EVENT_STALE_MS = 5 * 60_000;
const EVENT_GC_MS = 10 * 60_000;

export interface UseColumnEventsOptions {
    column: string;
    filters: BoardFilters;
    filterKeys?: readonly string[] | null;
    scoreGroups?: string[];
    limit?: number;
    enabled?: boolean;
}

export interface ColumnEventsResult {
    events: ScreenerEvent[];
    effectiveSortBy: string;
    effectiveGroup: string | null;
    isLoading: boolean;
    isFetching: boolean;
    rankIds: string[];
    pageCount: number;
    isSettled: boolean;
    error: Error | null;
    errorUpdatedAt: number;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    retry: () => void;
}

interface MergedEntry {
    row: BoardRankRow;
    base: ScreenerEvent;
    merged: ScreenerEvent;
}

export function useColumnEvents(opts: UseColumnEventsOptions): ColumnEventsResult {
    const { column, filters, filterKeys = null, enabled = true } = opts;
    const pageSize = opts.limit ?? PAGE_SIZE;
    const qc = useQueryClient();

    const signature = filterSignature(filters);
    const [limit, setLimit] = useState(pageSize);

    useEffect(() => {
        setLimit(pageSize);
    }, [column, signature, pageSize]);

    const rank = useQuery<BoardRankResponse, Error>({
        queryKey: boardKeys.rank(column, signature, limit),
        queryFn: ({ signal }) =>
            fetchBoardRank({
                column,
                group: filters.group,
                sortBy: filters.sortBy || undefined,
                sortDir: filters.sortDir,
                eventsInclude: filters.eventsInclude,
                eventsExclude: filters.eventsExclude,
                tagsInclude: filters.tagsInclude,
                tagsExclude: filters.tagsExclude,
                tagMatch: filters.tagMatch,
                filters,
                filterKeys,
                limit,
                signal,
            }),
        enabled: enabled && Boolean(filters.sortBy),
        staleTime: RANK_STALE_MS,
        gcTime: RANK_GC_MS,
        placeholderData: keepPreviousData,
    });

    const rows = rank.data?.rows ?? [];

    const rankIds = useMemo(() => (rank.data?.rows ?? []).map(r => r.event_id), [rank.data]);
    const pageCount = rankIds.length === 0 ? 0 : Math.ceil(rankIds.length / pageSize);
    const isSettled = rank.isSuccess && !rank.isPlaceholderData;

    const eventQueries = useQueries({
        queries: rows.map(r => ({
            queryKey: boardKeys.event(r.event_id),
            queryFn: () => getEventBatcher(qc).load(r.event_id),
            staleTime: EVENT_STALE_MS,
            gcTime: EVENT_GC_MS,
        })),
    });

    // Rank fields ride on the event object; keep one merged object per id so
    // memo on the card holds when neither the row nor the event changed.
    const mergeCache = useRef(new Map<string, MergedEntry>());
    const next = new Map<string, MergedEntry>();
    const events: ScreenerEvent[] = [];
    let anyEventLoading = false;

    rows.forEach((row, i) => {
        const q = eventQueries[i];
        const base = q?.data as ScreenerEvent | undefined;
        if (!base) {
            if (q?.isLoading) anyEventLoading = true;
            return;
        }
        const prev = mergeCache.current.get(row.event_id);
        let entry: MergedEntry;
        if (
            prev &&
            prev.base === base &&
            prev.row.sort_value === row.sort_value &&
            prev.row.primary_market_id === row.primary_market_id
        ) {
            entry = prev;
        } else {
            entry = {
                row,
                base,
                merged: {
                    ...base,
                    primary_market_id: row.primary_market_id ?? base.primary_market_id,
                    sort_value: row.sort_value,
                },
            };
        }
        next.set(row.event_id, entry);
        events.push(entry.merged);
    });
    mergeCache.current = next;

    const hasNextPage = Boolean(rank.data?.next_cursor) && limit < MAX_RANK_LIMIT;

    const fetchNextPage = useCallback(() => {
        if (!hasNextPage || rank.isFetching) return;
        setLimit(l => Math.min(l + pageSize, MAX_RANK_LIMIT));
    }, [hasNextPage, rank.isFetching, pageSize]);

    const retry = useCallback(() => {
        void rank.refetch();
    }, [rank]);

    return {
        events,
        effectiveSortBy: rank.data?.sort_by ?? filters.sortBy,
        effectiveGroup: rank.data?.group ?? filters.group,
        rankIds,
        pageCount,
        isSettled,
        isLoading: rank.isLoading || (rows.length > 0 && events.length === 0 && anyEventLoading),
        isFetching: rank.isFetching || eventQueries.some(q => q.isFetching),
        error: rank.error ?? null,
        errorUpdatedAt: rank.errorUpdatedAt,
        hasNextPage,
        isFetchingNextPage: rank.isPlaceholderData && rank.isFetching,
        fetchNextPage,
        retry,
    };
}